/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ComponentProps, type ReactNode, useMemo } from 'react';
import { cn } from '@/lib/utils';
import type { ShopperBasketsV2, ShopperOrders, ShopperProducts, ShopperSearch } from '@/scapi';
import CurrentPrice from './current-price';
import ListPrice from './list-price';
import PerUnitSubtitle from './per-unit-subtitle';
import PromoCallout from './promo-callout';
import UnavailablePrice from './unavailable-price';
import { getPriceData } from './utils';

type Product =
    | ShopperProducts.schemas['Product']
    | (ShopperBasketsV2.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
    | (ShopperOrders.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
    | ShopperSearch.schemas['ProductSearchHit'];

interface ProductPriceProps {
    labelForA11y?: string;
    product: Product;
    currency: string;
    quantity?: number;
    currentPriceProps?: Omit<ComponentProps<typeof CurrentPrice>, 'price' | 'currency' | 'labelForA11y'>;
    listPriceProps?: Omit<ComponentProps<typeof ListPrice>, 'price' | 'currency' | 'labelForA11y'>;
    promoCalloutProps?: Omit<ComponentProps<typeof PromoCallout>, 'product'>;
    /** Optional custom content rendered between price and promo callout */
    afterPriceContent?: ReactNode;
    type?: 'unit' | 'total';
    className?: string;
    /** Hide promotional callout text (e.g. in modal/edit mode) */
    hidePromo?: boolean;
    /** Show only the current price without "From" prefix, list price, or promo (e.g. in modal/edit mode) */
    currentPriceOnly?: boolean;
    /**
     * Color scheme for the sale price. Defaults to `'destructive'` (red) for PDP/PLP/cart.
     * Pass `'success'` to render the sale price in green (e.g. order confirmation context).
     */
    saleColorScheme?: 'destructive' | 'success';
    /**
     * Explicitly signal that this line item has an active discount, ensuring the
     * list-price strikethrough renders even when `getPriceData` cannot detect
     * `isOnSale` from price fields alone (e.g. bundle-component edge cases where
     * `price === basePrice` but `priceAdjustments` are non-empty).
     * Cart context: pass `!!(productItem.priceAdjustments?.length)`.
     */
    hasDiscount?: boolean;
    /**
     * Content rendered instead of the default "Price unavailable" label when the
     * product has no price in the shopper's active price books (legacy
     * NOT_AVAILABLE). PDP/PLP pass a "Check Price" CTA that opens the delivery-ZIP
     * flyout; contexts without a ZIP affordance (cart, order history) omit it and
     * get the neutral default.
     */
    unavailablePriceContent?: ReactNode;
    /**
     * Legacy `org_ashcomm` "set of multiples" parity for PLP tile and PDP
     * (`producttile_pricing.isml`, `pricing.isml`). When `true` AND the product's
     * `getPriceData().setOfMultiples` is also true (catalog `min-order-quantity`
     * > 1 AND `min-order-quantity === step-quantity`), the displayed current /
     * list price is multiplied by `setMultiplier` (`min-order-quantity`), and a
     * small `($<unit> each)` subtitle is rendered directly below. Cart line
     * items, mini-cart, and order-review intentionally leave this `false` so
     * they keep displaying per-unit prices (mirrors legacy `cart-line-item.isml`
     * + `minilineitems.isml` which only show a `(Set of N)` badge next to the
     * product name, not an "each" subtitle).
     */
    showPerUnitSubtitle?: boolean;
}

/**
 * @param product - product object containing price information
 * // If a product is a set,
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show price on PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param promoCalloutProps - extra props to be passing to PromoCallout component (className overrides default)
 * @param type - type of price to display. 'unit' for unit price, 'total' for total price (unit price * quantity).
 * @param labelForA11y - label to be used for a11y
 */
export default function ProductPrice({
    labelForA11y,
    product,
    currency,
    quantity = 1,
    type = 'total',
    currentPriceProps = {},
    listPriceProps = {},
    promoCalloutProps = {},
    afterPriceContent,
    className,
    hidePromo = false,
    currentPriceOnly = false,
    hasDiscount = false,
    unavailablePriceContent,
    saleColorScheme = 'destructive',
    showPerUnitSubtitle = false,
}: ProductPriceProps) {
    const priceData = useMemo(() => getPriceData(product, { quantity }), [product, quantity]);
    const {
        listPrice,
        currentPrice,
        isASet,
        isMaster,
        isOnSale,
        isRange,
        maxPrice,
        priceUnavailable,
        setOfMultiples,
        setMultiplier,
    } = priceData;

    // Legacy "set of multiples" headline gets the unit price *times* the
    // catalog `min-order-quantity` (e.g. $100 unit × 2 = $200 set); the small
    // `(<unit> each)` subtitle below carries the raw unit price. `applySetMultiplier`
    // gates on the opt-in prop + `setOfMultiples` flag from `getPriceData`, and is
    // suppressed for product sets (which already aggregate child prices) and
    // basket items (which always go through their own non-multiplier branch).
    const applySetMultiplier = showPerUnitSubtitle && setOfMultiples && !isASet && setMultiplier > 1;
    const headlineMultiplier = applySetMultiplier ? setMultiplier : 1;
    const renderPerUnit = applySetMultiplier;

    // No price from any active price book → render an unavailable state (legacy
    // NOT_AVAILABLE), never $0.00. Add to Cart is gated separately.
    if (priceUnavailable) {
        return (
            <div className={cn('items-center gap-2', className)}>
                {unavailablePriceContent ?? <UnavailablePrice labelForA11y={labelForA11y} />}
            </div>
        );
    }

    // Show strikethrough (list price) only for single price, not when displaying price range (min – max)
    const showPriceRange = isRange && maxPrice != null && maxPrice > currentPrice;
    const showListPrice = (isOnSale || hasDiscount) && listPrice && !showPriceRange;

    const saleColor = saleColorScheme === 'success' ? 'text-success' : 'text-destructive';
    const currentPriceColorClass = isOnSale ? saleColor : 'text-foreground';
    const mergedCurrentPriceProps = {
        ...currentPriceProps,
        className: cn(currentPriceColorClass, currentPriceProps.className),
    };

    if (currentPriceOnly) {
        return (
            <div className={cn('items-center gap-2', className)}>
                <CurrentPrice
                    labelForA11y={labelForA11y}
                    price={type === 'unit' ? currentPrice : currentPrice * quantity}
                    as="span"
                    currency={currency}
                    isRange={false}
                    {...mergedCurrentPriceProps}
                />
            </div>
        );
    }

    const renderCurrentPrice = (flag: boolean) => (
        <CurrentPrice
            labelForA11y={labelForA11y}
            price={(type === 'unit' ? currentPrice : currentPrice * quantity) * headlineMultiplier}
            as="span"
            currency={currency}
            isRange={flag}
            maxPrice={
                flag && maxPrice != null
                    ? (type === 'unit' ? maxPrice : maxPrice * quantity) * headlineMultiplier
                    : undefined
            }
            {...mergedCurrentPriceProps}
        />
    );

    const renderListPrice = (flag: boolean) =>
        listPrice && (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={currency}
                price={(type === 'unit' ? listPrice : listPrice * quantity) * headlineMultiplier}
                isRange={flag}
                {...listPriceProps}
            />
        );

    // Legacy `<span class="price-set">($X.XX each)</span>` — emitted directly
    // below the headline price when the product is sold as a "set of multiples".
    // Range branch uses `currentPrice` and `maxPrice` (both per-unit at this
    // point — we have NOT multiplied them through this path), matching the
    // legacy `<p class="price-set">(<min> – <max> each)</p>` shape.
    const renderPerUnitSubtitle = () =>
        renderPerUnit && (
            <PerUnitSubtitle
                unitPrice={currentPrice}
                unitMaxPrice={isRange && maxPrice != null && maxPrice > currentPrice ? maxPrice : undefined}
                currency={currency}
            />
        );

    const renderPriceSet = (flag: boolean) => (
        <>
            {renderCurrentPrice(flag)} {showListPrice && renderListPrice(flag)}
        </>
    );

    if (isASet) {
        return (
            <>
                <div className={cn('items-center gap-2', className)}>{renderCurrentPrice(true)}</div>
                {afterPriceContent}
                {!hidePromo && (
                    <PromoCallout
                        product={product}
                        {...promoCalloutProps}
                        className={cn(className, promoCalloutProps?.className)}
                    />
                )}
            </>
        );
    }

    if (isMaster) {
        return (
            <>
                <div className={cn('items-center gap-2', className)}>{renderPriceSet(isRange ?? false)}</div>
                {renderPerUnitSubtitle()}
                {afterPriceContent}
                {!hidePromo && (
                    <PromoCallout
                        product={product}
                        {...promoCalloutProps}
                        className={cn(className, promoCalloutProps?.className)}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <div className={cn('items-center gap-2', className)}>{renderPriceSet(isRange ?? false)}</div>
            {renderPerUnitSubtitle()}
            {afterPriceContent}
            {!hidePromo && (
                <PromoCallout
                    product={product}
                    {...promoCalloutProps}
                    className={cn(className, promoCalloutProps?.className)}
                />
            )}
        </>
    );
}
